export const REVENUECAT_ABSITE_MONTHLY_PRODUCT_ID = (
    process.env.REACT_APP_REVENUECAT_ABSITE_MONTHLY_PRODUCT_ID ||
    'com.medlearnity.qlearn.absite.monthly'
);

// The App Store Connect introductory offer on the monthly ABSITE product is a
// 1-week free trial. On iOS the trial comes from ASC (surfaced via RevenueCat),
// NOT the backend `trialPeriodDays` — that field drives the web Stripe trial and
// is empty on the plans served to the native build. Single source of truth for
// the dev-preview plan and the live-data enrichment (buildAbsiteMobilePaywallPlans).
export const REVENUECAT_ABSITE_TRIAL_PERIOD_DAYS = 7;

export const REVENUECAT_IOS_API_KEY = process.env.REACT_APP_REVENUECAT_IOS_API_KEY || '';

export const REVENUECAT_ANDROID_API_KEY = process.env.REACT_APP_REVENUECAT_ANDROID_API_KEY || '';

export const isMobileDevPaywallEnabled = () => (
    /^(1|true|yes)$/i.test(process.env.REACT_APP_QLEARN_MOBILE_DEV_PAYWALL || '')
);

export const isQlearnMobileBuildEnabled = () => (
    /^(1|true|yes)$/i.test(process.env.REACT_APP_QLEARN_MOBILE_BUILD || '')
);

export const MOBILE_DEV_PAYWALL_USER = {
    id: 'qlearn-absite-ios-dev-user',
    email: 'ios-preview@qlearn.local'
};

export const MOBILE_DEV_PAYWALL_ABSITE_PLAN = {
    id: 'qlearn-absite-ios-dev-plan',
    name: 'QLearn ABSITE Pro Monthly',
    price: 59,
    durationInDay: 30,
    // Mirrors the App Store Connect introductory offer so the local preview
    // paywall exercises the trial presentation — the same value the live native
    // paywall stamps onto the real ABSITE plan (see buildAbsiteMobilePaywallPlans).
    trialPeriodDays: REVENUECAT_ABSITE_TRIAL_PERIOD_DAYS,
    questionsPerDay: 'Unlimited',
    examTypes: ['ABSITE'],
    // Same product binding the live paywall stamps on the real row — the
    // confirmation handler refuses any unbound plan (GPT-5.6 finding 1).
    revenueCatProductId: REVENUECAT_ABSITE_MONTHLY_PRODUCT_ID,
    // Customer-plausible copy: preview builds get screenshotted for design
    // review, and internal configuration notes must never appear in a
    // rendered surface (UX-sweep ledger A1).
    description: 'Unlimited ABSITE practice questions with detailed guidance, performance tracking, and daily study tools.'
};

export const MOBILE_BILLING_REASONS = {
    DEV_PAYWALL_STORE_DISABLED: 'DEV_PAYWALL_STORE_DISABLED',
    ELIGIBILITY_CHECK_FAILED: 'ELIGIBILITY_CHECK_FAILED',
    NOT_NATIVE_RUNTIME: 'NOT_NATIVE_RUNTIME',
    MISSING_REVENUECAT_API_KEY: 'MISSING_REVENUECAT_API_KEY',
    PRODUCT_NOT_AVAILABLE: 'PRODUCT_NOT_AVAILABLE',
    PURCHASE_CANCELLED: 'PURCHASE_CANCELLED',
    PURCHASE_TIMED_OUT: 'PURCHASE_TIMED_OUT',
    // A pre-sheet dependency (eligibility fetch, RC configure, offerings) hung
    // past its bound — the sheet was NEVER opened; safe to retry immediately.
    SETUP_TIMED_OUT: 'SETUP_TIMED_OUT',
    RESTORE_TIMED_OUT: 'RESTORE_TIMED_OUT',
    REVENUECAT_CONFIGURE_FAILED: 'REVENUECAT_CONFIGURE_FAILED',
    REVENUECAT_OFFERINGS_FAILED: 'REVENUECAT_OFFERINGS_FAILED',
    REVENUECAT_PURCHASE_FAILED: 'REVENUECAT_PURCHASE_FAILED',
    REVENUECAT_RESTORE_FAILED: 'REVENUECAT_RESTORE_FAILED',
    NOTHING_TO_RESTORE: 'NOTHING_TO_RESTORE',
    UNSUPPORTED_PLATFORM: 'UNSUPPORTED_PLATFORM'
};

const isMonthlyDuration = (durationInDay) => {
    const days = Number.parseInt(durationInDay, 10);
    return Number.isFinite(days) && days >= 28 && days <= 31;
};

// Identifies the backend plan that maps to the single purchasable native product
// (REVENUECAT_ABSITE_MONTHLY_PRODUCT_ID): the monthly ABSITE subscription. Matches
// on the exam tag or plan name so it survives either backend payload shape.
const isAbsiteMonthlyPlan = (plan) => {
    if (!plan || !isMonthlyDuration(plan.durationInDay)) {
        return false;
    }

    const examTypes = Array.isArray(plan.examTypes) ? plan.examTypes : [];
    const taggedAbsite = examTypes.some((examType) => String(examType).toUpperCase() === 'ABSITE');

    return taggedAbsite || /absite/i.test(plan.name || '');
};

// The native trial is an App Store Connect introductory offer surfaced through
// RevenueCat, not the backend Stripe `trialPeriodDays` (empty on native payloads).
// Stamp the ASC offer so "Try Pro free for 1 week" renders on live data, while
// still honouring a backend-configured trial length if one is ever present.
export // The stamped trialPeriodDays is a PLACEHOLDER ONLY (row identity/selection
// plumbing): every native trial/price DISCLOSURE binds to the live StoreKit
// package via getAbsiteMobilePackagePricing (round-2 finding 1) — a backend
// row claiming its own trial length (e.g. 14 days vs the App Store's 7) must
// never reach customer copy, so the backend value is deliberately IGNORED.
const withAbsiteMobileTrialOffer = (plan) => ({
    ...plan,
    trialPeriodDays: REVENUECAT_ABSITE_TRIAL_PERIOD_DAYS
});

// Native (non-dev) paywall plan set. The App Store purchase is hard-wired to the
// single monthly ABSITE product, so present exactly that one plan — carrying the
// ASC trial and TAGGED with the StoreKit product it binds to — and FAIL CLOSED
// (empty paywall) when the backend payload has no recognizable ABSITE monthly
// row. The old fallback returned the untouched multi-plan web catalogue, which
// let the UI disclose an annual/USMLE row while the purchase path bought the
// fixed monthly ABSITE product (gate leg GPT-5.6 finding 1) — an empty state is
// honest; a mispriced disclosure on the money path is not.
export const buildAbsiteMobilePaywallPlans = (plans) => {
    const planList = Array.isArray(plans) ? plans : [];
    const absiteMonthlyPlan = planList.find(isAbsiteMonthlyPlan);

    if (!absiteMonthlyPlan) {
        return [];
    }

    return [{
        ...withAbsiteMobileTrialOffer(absiteMonthlyPlan),
        revenueCatProductId: REVENUECAT_ABSITE_MONTHLY_PRODUCT_ID
    }];
};
